/**
 * sl-apue: SeatUpsellDialog — Offers to purchase additional seats when member limit is reached
 * sl-7vu4: Uses graduated tiered pricing instead of flat per-seat price
 */
import { ConfirmModal, notify } from '@affine/component';
import { SeatAddonService } from '@affine/core/modules/cloud';
import { formatPrice } from '@affine/core/utils/format-price';
import {
  calculateGraduatedTotal,
  parseSeatTiers,
} from '@affine/core/utils/graduated-pricing';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface SeatUpsellDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  seatsNeeded: number;
  onSuccess: () => void;
  onUpgrade: () => void;
}

export const SeatUpsellDialog = ({
  open,
  setOpen,
  seatsNeeded,
  onSuccess,
  onUpgrade,
}: SeatUpsellDialogProps) => {
  const t = useI18n();
  const seatAddonService = useService(SeatAddonService);
  const memberQuota = useLiveData(seatAddonService.seatAddon.memberQuota$);
  const addon = useLiveData(seatAddonService.seatAddon.addon$);

  const [isPurchasing, setIsPurchasing] = useState(false);

  useEffect(() => {
    if (open) {
      seatAddonService.seatAddon.revalidate();
    }
  }, [open, seatAddonService]);

  const currentExtraSeats =
    addon?.status === 'active' ? (addon.quantity ?? 0) : 0;
  const totalNeeded = currentExtraSeats + seatsNeeded;
  const maxExtraSeats = memberQuota?.maxExtraSeats ?? 0;
  const exceedsPlanLimit = maxExtraSeats !== -1 && totalNeeded > maxExtraSeats;

  const seatTiers = useMemo(
    () => parseSeatTiers(memberQuota?.seatTiers),
    [memberQuota?.seatTiers]
  );

  // Calculate the INCREMENTAL cost: total for new quantity minus total for current quantity
  const additionalCost = useMemo(() => {
    if (!seatTiers?.monthly) return 0;
    const costAfter = calculateGraduatedTotal(seatTiers.monthly, totalNeeded);
    const costBefore = calculateGraduatedTotal(
      seatTiers.monthly,
      currentExtraSeats
    );
    return costAfter - costBefore;
  }, [seatTiers, totalNeeded, currentExtraSeats]);

  const handlePurchase = useCallback(async () => {
    if (exceedsPlanLimit) return;
    setIsPurchasing(true);
    try {
      if (currentExtraSeats === 0) {
        await seatAddonService.seatAddon.purchaseSeats(totalNeeded);
      } else {
        await seatAddonService.seatAddon.updateSeatCount(totalNeeded);
      }
      notify.success({
        title: t['com.streamlain.payment.seat-upsell.success'](),
      });
      setOpen(false);
      onSuccess();
    } catch (err) {
      notify.error({
        title: t['com.streamlain.payment.seat-addon.error'](),
      });
      console.error('Failed to purchase seats:', err);
    } finally {
      setIsPurchasing(false);
    }
  }, [
    exceedsPlanLimit,
    currentExtraSeats,
    totalNeeded,
    seatAddonService,
    t,
    setOpen,
    onSuccess,
  ]);

  return (
    <ConfirmModal
      open={open}
      onOpenChange={setOpen}
      title={t['com.streamlain.payment.seat-upsell.title']()}
      description={t['com.streamlain.payment.seat-upsell.description']({
        seatsNeeded: String(seatsNeeded),
        cost: formatPrice(additionalCost, 'eur'),
      })}
      confirmText={t['com.streamlain.payment.seat-upsell.purchase']()}
      onConfirm={() => void handlePurchase()}
      confirmButtonOptions={{
        loading: isPurchasing,
        disabled: isPurchasing || exceedsPlanLimit,
      }}
      cancelText={t['com.streamlain.payment.seat-upsell.upgrade-plan']()}
      onCancel={onUpgrade}
    />
  );
};
